import TextareaAutosize from 'react-textarea-autosize'
import { motion } from 'framer-motion'
import { observer } from 'mobx-react'
import React, { Fragment, useEffect, useState } from 'react'
import { ChatFeed } from 'react-chat-ui'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import ReactLoading from 'react-loading'
import { useNavigate, useSearchParams } from 'react-router-dom'
import _, { indexOf } from 'lodash'

import userMobx from '../../mobx/user'
import utilsMobx from '../../mobx/utils'
import {
  AllMessages,
  BriefApActif,
  ClientI,
  GroupedMessages,
  TypeClient,
  TypeCompanyRole,
  Vogzter,
} from '../../types-project/Client'
import BrandSwitcher from '../../utils/components/BrandSwitcher/BrandSwitcher'
import Btn1 from '../../utils/components/Button/Btn1'
import SearchBar from '../../utils/components/SearchBar'
import { countMsgNRead, getAvatar, readMessageSocket, variantPage } from '../../utils/helpers'
import Bubble from './components/Bubble'
import CardMsg from './components/Card-message'
import styles from './components/styles.module.scss'
import BtnBack from '../../utils/components/Button/BtnBack'
import ChatMessages from './components/ChatMessages'
import BtnSendMessage from './components/ChatMessages/components/BtnSend'
import { Message } from './components/Bubble/components/message'
import VogzterPicture from '../Favorites-vogzters/Profil-vogzters/components/VogzterPicture'
import briefR from '../../mobx/brief'
import modal from '../../mobx/modal'
import MessageCreditsBadge from '../../utils/components/CampaignInvite/MessageCreditsBadge'
import { useCampaignInvites } from '../../utils/components/CampaignInvite/useCampaignInvites'
import {
  InviteStatsBanner,
  InviteContextCard,
} from '../../utils/components/CampaignInvite/InviteFunnel'
import SendTips, { canReceiveTips } from '../../utils/components/Tips'

const Chat = observer(ChatFeed)

const Messaging = () => {
  const navigate = useNavigate()
  // text message to be send
  const [textMessage, setTextMessage] = useState<string>('')
  const [showMessages, setShowMessages] = useState<boolean>(true)
  const [
    selectedIdUserCurrentMessages,
    setSelectedIdUserCurrentMessages,
  ] = useState<string>("")
  const [search, setSearch] = useState('')
  const filterSearch = (groups: GroupedMessages[]) => {
    if (!search) {
      return groups
    }
    const matchingGroup = groups.filter((group) => {
      return group.messages.some((message) => {
        const messageText = (message.text || '').toLowerCase()
        const searchTerms = search.toLowerCase()
        const firstName = (
          (message.user as Vogzter)?.first_name ||
          ''
        ).toLowerCase()
        const lastName = (
          (message.user as Vogzter)?.last_name ||
          ''
        ).toLowerCase()
        // Check if any of the search terms appear in the message text or sender's first name
        return (
          messageText?.includes(searchTerms) ||
          message.sender?.includes(searchTerms) ||
          firstName?.includes(searchTerms) ||
          lastName?.includes(searchTerms)
        )
      })
    })
    return matchingGroup
  }

  // getting the brand infos from the local storage
  const userInfos = userMobx.getUserInfos() as ClientI
  const brand =
    userInfos._company.type === 'agency'
      ? userMobx.getBrandSession()!
      : userMobx.brand.data

  useEffect(() => {
    briefR.getAllBoughtMedia()
    userMobx.getMe()
    userMobx.getAllMsgBrand()
  }, [])

  useEffect(() => {
    setShowMessages(utilsMobx.getWidth > utilsMobx.mobileMaxWidth)
  }, [utilsMobx.window.width])

  const { loading, data, error } = userMobx.messages

  // Deep-link depuis le toast "nouveau message" (/dashboard/messaging?u=<id>) :
  // une fois la liste chargee, on ouvre la conversation puis on nettoie le param.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const u = searchParams.get('u')
    if (u && data?.some((g) => g.user?._id === u)) {
      setSelectedIdUserCurrentMessages(u)
      setShowMessages(true)
      searchParams.delete('u')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, data])
  const allMsgUser = data?.filter(
    (group) => (group?.brief_applications?.length ?? 0) > 0 || (userMobx.brand.data?.favourites ?? [])?.includes(group.user?._id)
  )

  const msgArchived = data?.filter(
    (group) =>
      (group?.brief_applications?.length ?? 0) === 0 && !(userMobx.brand.data?.favourites ?? [])?.includes(group.user?._id)
  )

  const countMsgArchivedNRead = countMsgNRead(
    msgArchived!.flatMap((group) => group.messages)
  )

  // Total de messages non lus (toutes conversations) -> pilote l'affichage du
  // bouton "Tout marquer comme lu" et du filtre "Non lus".
  const totalUnread = countMsgNRead((data ?? []).flatMap((group) => group.messages))

  const [filterTypeConversation, setFilterTypeConversation] = useState<
    'all' | 'archived' | 'invited' | 'unread'
  >('all')

  // Conversations contenant au moins un message entrant non lu (filtre "Non lus").
  const allUnreadMsgUser = data?.filter(
    (group) => countMsgNRead(group.messages) > 0
  )

  // Si on est sur le filtre "Non lus" et qu'il n'y a plus rien (ex. apres
  // "tout marquer comme lu"), on revient sur "Tout" pour eviter une liste vide.
  useEffect(() => {
    if (filterTypeConversation === 'unread' && totalUnread === 0)
      setFilterTypeConversation('all')
  }, [totalUnread, filterTypeConversation])
  const { t } = useTranslation()

  // Invitations campagne de la marque : funnel global + index par createur.
  // On resout l'id via getSelectedBrandId() (fiable des le 1er rendu, gere
  // marque/agence/guest) plutot que brand?._id qui depend de userMobx.brand.data
  // souvent encore null au chargement de la messagerie.
  const { byUser: invitesByUser, invitedUserIds, stats: inviteStats } =
    useCampaignInvites(userMobx.getSelectedBrandId())
  // Conversations des createurs invites (peuvent ne pas etre en favori ni avoir
  // de candidature : on les surface explicitement via l'onglet "Invitations").
  const allInvitedMsgUser = data?.filter((group) =>
    invitedUserIds.has(group.user?._id)
  )
  const listForFilter =
    filterTypeConversation === 'archived'
      ? msgArchived!
      : filterTypeConversation === 'invited'
        ? allInvitedMsgUser ?? []
        : filterTypeConversation === 'unread'
          ? allUnreadMsgUser ?? []
          : allMsgUser!
  const sendMessage = () => {
      userMobx.sendMessage(textMessage, selectedIdUserCurrentMessages!, brand?._id!);
      setTextMessage('')
  }
  const allMsgUserSelected = data!
    .filter((group) => group.user?._id === selectedIdUserCurrentMessages)
    .flatMap((group) => group.messages)
  const conversationSelected = data!
    .filter((group) => group.user?._id === selectedIdUserCurrentMessages)
    .flatMap((group) =>
      group.messages.flatMap(
        (message) =>
          new Message({
            id: message._id,
            message: message.text,
            senderName: message.sender,
            createdAt: message.createdAt,
            read: message.read && message.sender === 'client',
            lastOne: _.last(group.messages)?._id === message._id,
            offer: message.offer,
            brief: message.brief
          })
      )
    )
  useEffect(() => {
    readMessageSocket(allMsgUserSelected)
  }, [allMsgUserSelected])
  const userSelectedBa = allMsgUser?.find(
    (group) => group.user?._id === selectedIdUserCurrentMessages
  )
  const userSelected = allMsgUserSelected.at(allMsgUserSelected?.length - 1)
    ?.user as Vogzter
  const dedupeByBrief = (list: BriefApActif[]) =>
    list.filter(
      (briefAp, i, array) =>
        array.findIndex((other) => other?.brief?._id === briefAp.brief?._id) === i
    )
  // Raccourcis : une pastille par campagne en cours.
  const uniqueBriefApplications = dedupeByBrief(userSelectedBa?.brief_applications ?? [])
  // Tips : les collabs gagnees (campagne cloturee comprise) ET les collabs en
  // cours dont le createur n'est pas sorti du flow.
  const tipsTargets = dedupeByBrief([
    ...(userSelectedBa?.tips_brief_applications ?? []),
    ...uniqueBriefApplications.filter((briefAp) => canReceiveTips(briefAp.status)),
  ]).map((briefAp) => ({ _id: briefAp._id, name: briefAp.brief?.name }))
  return (
    <main className={`h-full ${styles['container-messaging']}`}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Vogz - Messaging</title>
      </Helmet>
      <>
        <motion.section
          variants={variantPage} // Pass the variant object into Framer Motion
          initial="hidden" // Set the initial state to variants.hidden
          animate="enter" // Animated state to variants.enter
          exit="exit" // Exit state (used later) to variants.exit
          transition={{ type: 'linear' }} // Set the transition to linear
          onAnimationComplete={() => {
            utilsMobx.handleAssideEnded(true)
          }}
          className={`
            ${styles['all-page']}
            ${utilsMobx.aside_animation_ended ? '!transform-none' : ''}`}
        >
          <section className={styles['section-list-messages']}>
            <div className={styles['header-bar']}>
              {userMobx.me.data?.company_role !== TypeCompanyRole.GUEST && (
                <BrandSwitcher dntComesFromBriefList />
              )}
              <div className="ml-auto flex items-center gap-2">
                {totalUnread > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      userMobx.markAllMessagesRead(userMobx.getSelectedBrandId())
                    }
                    title={t('messages:mark-all-read', 'Tout marquer comme lu')}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#e3e6f5] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#7885ed] transition hover:bg-[#eef0fb]"
                  >
                    <i className="fa-solid fa-check-double" />
                    {t('messages:mark-all-read', 'Tout marquer comme lu')}
                  </button>
                )}
                <MessageCreditsBadge />
              </div>
            </div>
            {filterTypeConversation === 'archived' ?
              <BtnBack btnName={t('mycastings:return')} customAction={() => setFilterTypeConversation('all')} />
              : null}
            <div className={styles['header-list']}>
              <SearchBar
                inputClass="sm:!w-[280px]"
                onChange={(v) => {
                  setSearch(v.target.value ?? undefined)
                }}
              />
              {totalUnread > 0 && (
                <button
                  aria-pressed={
                    filterTypeConversation === 'unread' ? 'true' : 'false'
                  }
                  type="button"
                  onClick={() => {
                    if (filterTypeConversation === 'unread')
                      setFilterTypeConversation('all')
                    else setFilterTypeConversation('unread')
                    setSelectedIdUserCurrentMessages('')
                  }}
                >
                  <p>{t('messages:chat-unread', 'Non lus')}</p>
                  <span>{totalUnread}</span>
                </button>
              )}
              {invitedUserIds.size > 0 && (
                <button
                  aria-pressed={
                    filterTypeConversation === 'invited' ? 'true' : 'false'
                  }
                  type="button"
                  onClick={() => {
                    if (filterTypeConversation === 'invited')
                      setFilterTypeConversation('all')
                    else setFilterTypeConversation('invited')
                    setSelectedIdUserCurrentMessages('')
                  }}
                >
                  <p>{t('messages:chat-invited', 'Invitations')}</p>
                  <span>{invitedUserIds.size}</span>
                </button>
              )}
              <button
                aria-pressed={
                  filterTypeConversation === 'archived' ? 'true' : 'false'
                }
                type="button"
                onClick={() => {
                  if (filterTypeConversation === 'archived')
                    setFilterTypeConversation('all')
                  else setFilterTypeConversation('archived')
                  setSelectedIdUserCurrentMessages('')
                }}
              >
                <p>{t('messages:chat-archived')}</p>
                {countMsgArchivedNRead > 0 ? (
                  <span>{countMsgArchivedNRead}</span>
                ) : null}
              </button>
            </div>
            {filterTypeConversation === 'invited' && (
              <div className="px-3 pb-2">
                <InviteStatsBanner stats={inviteStats} />
              </div>
            )}
            <div className={styles['list-messages']}>
              <>
                {!loading &&
                  !error &&
                  data &&
                  filterSearch(listForFilter).length > 0 ? (
                  filterSearch(listForFilter)
                    .sort((a, b) => new Date(b.messages.at(b.messages.length - 1)?.createdAt!).getTime() - new Date(a.messages.at((a.messages?.length ?? 0) - 1)?.createdAt!).getTime())
                    .map((msgUser, k) => (
                      <CardMsg
                        key={k}
                        message={msgUser as GroupedMessages}
                        isInvited={invitedUserIds.has(
                          (msgUser as GroupedMessages).user?._id
                        )}
                        selectedIdUserCurrentMessages={
                          selectedIdUserCurrentMessages
                        }
                        setSelectedIdUserCurrentMessages={
                          (id: string) => {
                            setSelectedIdUserCurrentMessages(id)
                            if (!showMessages) setShowMessages(true)
                          }
                        }
                      />
                    ))
                ) : (
                  <div className={styles['placeholder-list-msg']}>
                    <img
                      src="/assets/imgs/placeholder-list-msg.png"
                      alt="decorative"
                    />
                    <h3>{t('messages:placeholder.list.title')}</h3>
                    <p>
                      {t('messages:placeholder.list.desc1')}{' '}
                      <span
                        aria-label="emojis visage fatigué et triste"
                        role="img"
                      >
                        😪
                      </span>{' '}
                      {t('messages:placeholder.list.desc2')}
                    </p>
                    <Btn1
                      onclick={() => {
                        // Non-premium : on garde le CTA actif mais on redirige
                        // vers le paywall au lieu de le griser.
                        if (!userMobx.me.data?._company.premium) {
                          navigate('/dashboard/premium')
                          return
                        }
                        if ((userMobx.brandSession.data !== null || userMobx.getBrandSession() !== null || 
                            (userMobx.me.data?._company.type === TypeClient.BRAND ))&&
                        userMobx.me.data !== null)
                          navigate('/dashboard/new-castings')
                      }}
                      btnName={t('messages:placeholder.list.btn')}
                    />
                  </div>
                )}
              </>
            </div>
          </section>
          <hr />
          {showMessages && (
            <ChatMessages
              conversation={conversationSelected ?? []}
              onChangeTextMessage={(v) => { setTextMessage(v) }}
              textMessage={textMessage}
              classnameContainer="!z-50"
              sendMessage={sendMessage}
              disabledTextMessage={selectedIdUserCurrentMessages === undefined ||
                (filterTypeConversation === 'archived' &&
                  !((userMobx.brand.data?.favourites ?? []).includes(
                    selectedIdUserCurrentMessages!
                  )))}
              renderHeader={
                selectedIdUserCurrentMessages ? (<Fragment>
                  <button
                    className={styles['btn-close-msg']}
                    type="button"
                    onClick={() => {
                      setShowMessages(false)
                      setSelectedIdUserCurrentMessages('')
                    }}
                  >
                    <i className="fa-regular fa-xmark" />
                  </button>
                  <section>
                    <div className={styles['profil']}>
                      <VogzterPicture
                        imgSource={userSelected?.profile?.picture?.source}
                        gender={userSelected?.profile?.gender}
                        size={50}
                        showCertified={userSelected?.certified}
                        lastActivity={userSelected?.last_activity}
                        href={`/dashboard/profil-vogzter/${selectedIdUserCurrentMessages}`}
                      />
                      <div className={styles['profil-info']}>
                        <h3>
                          {userSelected?.first_name}{' '}
                          {userSelected?.last_name}.{' '}
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                (userMobx.brand.data?.favourites ?? [])?.includes(
                                  selectedIdUserCurrentMessages
                                )
                              ) {
                                userMobx.deleteFavouriteVogzters(
                                  selectedIdUserCurrentMessages!
                                )
                              } else {
                                userMobx.addFavouriteVogzters(
                                  selectedIdUserCurrentMessages!
                                )
                                if (filterTypeConversation === 'archived')
                                  setFilterTypeConversation('all')
                              }
                            }}
                            className={`${filterTypeConversation === 'archived'
                              ? '!text-4xl pl-12'
                              : ''
                              }`}
                          >
                            {(userMobx.brand.data?.favourites ?? [])?.includes(
                              selectedIdUserCurrentMessages
                            ) ? (
                              <i className="fa-solid fa-heart top-5 right-6 text-[#F41A1A] z-[3]" />
                            ) : (
                              <i className="fa-solid fa-heart top-5 right-6 text-[#FFD1D1] z-[3]" />
                            )}
                          </button>
                        </h3>
                        <p>
                          {userSelected?.certified
                            ? t('messages:vogzter-certified')
                            : null}
                        </p>
                      </div>
                    </div>
                    <br />
                    {invitesByUser.has(selectedIdUserCurrentMessages) && (
                      <InviteContextCard
                        invites={
                          invitesByUser.get(selectedIdUserCurrentMessages) ?? []
                        }
                      />
                    )}
                    {uniqueBriefApplications.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                          {uniqueBriefApplications.map((briefAp) => (
                            <button
                              key={briefAp._id}
                              type="button"
                              className="inline-flex items-center justify-center gap-2 rounded-[5px] border border-[#5777FF] bg-[#F5F7FF] px-4 py-[0.45rem] font-hurmeBold text-[0.8rem] leading-none text-[#5777FF] transition-colors duration-200 hover:bg-[#5777FF] hover:text-white"
                              onClick={() => {
                                if (filterTypeConversation === 'all') {
                                  if (briefAp.brief.private?.is_private ?? false)
                                    navigate(`/dashboard/castings/${briefAp.brief?._id!}#messages`)
                                  else
                                    navigate(`/dashboard/castings/${briefAp.brief?._id!}/briefapplication/${briefAp._id}#messages`)
                                }
                              }}
                            >
                              <i className="fa-solid fa-bullhorn" />
                              {briefAp.brief?.name!}
                            </button>
                          ))}
                          {userMobx.me.data?.company_role !== TypeCompanyRole.GUEST && (
                            <SendTips targets={tipsTargets} />
                          )}
                        </div>
                      )}
                  </section>
                  <hr />
                </Fragment>) : undefined
              }
              renderCustomSendButton={
                <Fragment>
                  {userMobx.messages.loading ? (
                    <ReactLoading
                      type="spokes"
                      color="#000"
                      width={22}
                      className="flex items-center"
                    />
                  ) : <BtnSendMessage sendMessage={sendMessage} />}
                </Fragment>
              } />
          )}
        </motion.section>
      </>
    </main>
  )
}

export default observer(Messaging)
